/* ONAM's own marks: line glyphs and the grid loader. No partner logos or data here, so the header and
   the hub (both in MAP's main bundle) can use them without pulling in the ONAM workspace. */

import React from "react";

export const reducedMotion = () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ---------- glyphs (16 px line icons) ---------- */
const G: Record<string, React.ReactNode> = {
  arrow: <path d="M3 8h10M9 4l4 4-4 4" />, chevron: <path d="M6 4l4 4-4 4" />, close: <path d="M4 4l8 8M12 4l-8 8" />, add: <path d="M8 3v10M3 8h10" />,
  check: <path d="M3 8.5l3 3 7-7" />, search: <><circle cx="7" cy="7" r="4.5" /><path d="M10.4 10.4l3.1 3.1" /></>,
  external: <path d="M9 2.5h4.5V7M13.5 2.5 7.5 8.5M12 9.5v3a1 1 0 0 1-1 1H3.5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3" />,
  sliders: <><path d="M2.5 5h11M2.5 11h11" /><circle cx="5.5" cy="5" r="1.6" /><circle cx="10.5" cy="11" r="1.6" /></>,
  activity: <path d="M1.5 8h3l2-4.5 3 9 2-4.5h3" />,
  portal: <><path d="M1.5 8h6" /><rect x="8.5" y="3" width="6" height="10" rx="1" /><circle className="f" cx="11.5" cy="8" r="1.2" /></>,
  orbit: <><circle cx="8" cy="8" r="5.5" /><circle className="f" cx="11.9" cy="4.1" r="1.7" /></>,
  federation: <><path d="M2.5 10.5h11" /><circle className="f" cx="4" cy="10.5" r="1.6" /><circle className="f" cx="8" cy="10.5" r="1.6" /><circle className="f" cx="12" cy="10.5" r="1.6" /></>,
  trash: <path d="M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.6 8.5h5.8l.6-8.5" />,
  refresh: <path d="M13 3.5v3h-3M3 12.5v-3h3M12.4 6.5A4.8 4.8 0 0 0 4 5.2M3.6 9.5A4.8 4.8 0 0 0 12 10.8" />,
  help: <><circle cx="8" cy="8" r="6" /><path d="M6.3 6.3a1.8 1.8 0 1 1 2.5 1.7c-.5.2-.8.6-.8 1.1v.4" /><circle className="f" cx="8" cy="11.4" r=".8" /></>,
  notes: <><rect x="3" y="2" width="10" height="12" rx="1" /><path d="M5.5 5.5h5M5.5 8h5M5.5 10.5h3" /></>,
  logout: <path d="M6.5 13.5H3.5a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1h3M10.5 11l3-3-3-3M13.5 8h-7" />,
  info: <><circle cx="8" cy="8" r="6" /><path d="M8 7.2v3.6" /><circle className="f" cx="8" cy="5.1" r=".85" /></>,
  mail: <><rect x="2" y="3.5" width="12" height="9" rx="1" /><path d="M2.5 4.5 8 9l5.5-4.5" /></>,
};
export function Glyph({ n, label }: { n: string; label?: string }) {
  return <svg className="g" viewBox="0 0 16 16" {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}>{G[n]}</svg>;
}

/* ---------- the loader: the grid mark, its Azure tile stepping round the sources ---------- */
const TILES16: [number, number][] = [[1, 1], [6, 1], [11, 1], [1, 6], [6, 6], [11, 6], [1, 11], [6, 11], [11, 11]];
const PATH16: [number, number][] = [[11, 1], [11, 6], [11, 11], [6, 11], [1, 11], [1, 6], [1, 1], [6, 1]];
export function Loader16() {
  return (
    <svg className="onam-ld" viewBox="0 0 16 16" aria-hidden="true">
      {TILES16.map(([x, y], i) => <rect key={"t" + i} className="onam-t" x={x} y={y} width={4} height={4} rx={1} />)}
      {PATH16.map(([x, y], n) => <rect key={"a" + n} className={`onam-a onam-k${n}`} style={{ ["--n" as any]: n }} x={x} y={y} width={4} height={4} rx={1} />)}
    </svg>
  );
}
const s64 = (v: number) => 7 + v * 18;
export function Loader64({ lap, laps }: { lap?: string; laps?: number }) {
  const t: [number, number][] = [[0, 0], [1, 0], [2, 0], [0, 1], [1, 1], [2, 1], [0, 2], [1, 2], [2, 2]];
  const p: [number, number][] = [[2, 0], [2, 1], [2, 2], [1, 2], [0, 2], [0, 1], [0, 0], [1, 0]];
  return (
    <svg className="onam-ld" viewBox="0 0 64 64" role="img" aria-label="ONAM" style={{ ...(lap ? { ["--onam-lap" as any]: lap } : {}), ...(laps ? { ["--onam-laps" as any]: laps } : {}) }}>
      {t.map(([x, y], i) => <rect key={"t" + i} className="onam-t" x={s64(x)} y={s64(y)} width={14} height={14} rx={3.5} />)}
      {p.map(([x, y], n) => <rect key={"a" + n} className={`onam-a onam-k${n}`} style={{ ["--n" as any]: n }} x={s64(x)} y={s64(y)} width={14} height={14} rx={3.5} />)}
    </svg>
  );
}
export const Orb = () => <span className="orb"><Loader16 /></span>;

